import { TypedPolicy, PolicyType } from '../types';

import { ChangeConfirmationPolicyForm } from './change-confirmation/ChangeConfirmationPolicyForm';
import { DockerSetupPolicyForm } from './docker/setup/DockerSetupPolicyForm';
import { DockerRBACPolicyForm } from './docker/rbac/DockerRBACPolicyForm';
import { DockerSecurityPolicyForm } from './docker/security/DockerSecurityPolicyForm';
import { DockerRegistryPolicyForm } from './docker/registry/DockerRegistryPolicyForm';
import { KubernetesSecurityPolicyForm } from './kubernetes/security/KubernetesSecurityPolicyForm';
import { KubernetesRegistryPolicyForm } from './kubernetes/registry/KubernetesRegistryPolicyForm';
import { KubernetesRBACPolicyForm } from './kubernetes/rbac/KubernetesRBACPolicyForm';
import { KubernetesSetupPolicyForm } from './kubernetes/setup/KubernetesSetupPolicyForm';
import { DockerCleanupPolicyForm } from './docker/cleanup/DockerCleanupPolicyForm';
import { ObservabilityK8sPolicyForm } from './kubernetes/observability/ObservabilityK8sPolicyForm';

interface Props {
  type: PolicyType;
  mode: 'create' | 'edit';
  onSubmit: (values: unknown) => Promise<void>;
  policy?: TypedPolicy<unknown>;
  /** Template data for pre-filling form in create mode */
  templateData?: Record<string, unknown>;
  /** Wizard navigation - only for create mode */
  onBack?: () => void;
  canGoBack?: boolean;
}

/**
 * Helper component to render the correct policy form based on policy type.
 *
 * This component acts as a dispatcher, selecting the appropriate form component
 * based on the policy type. All forms follow the same interface and can be used
 * in both create and edit modes.
 *
 * Usage:
 * - Create mode: Pass `mode="create"` with `onBack` and `canGoBack` for wizard navigation
 * - Edit mode: Pass `mode="edit"` with `policy` for initial values
 */
export function PolicyFormByType({
  type,
  mode,
  onSubmit,
  policy,
  templateData,
  onBack,
  canGoBack,
}: Props) {
  switch (type) {
    case 'change-confirmation':
      return (
        <ChangeConfirmationPolicyForm
          mode={mode}
          onSubmit={onSubmit}
          policy={policy}
          templateData={templateData}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      );
    case 'setup-docker':
      return (
        <DockerSetupPolicyForm
          mode={mode}
          onSubmit={onSubmit}
          policy={policy}
          templateData={templateData}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      );
    case 'rbac-docker':
      return (
        <DockerRBACPolicyForm
          mode={mode}
          onSubmit={onSubmit}
          policy={policy}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      );
    case 'security-docker':
      return (
        <DockerSecurityPolicyForm
          mode={mode}
          onSubmit={onSubmit}
          policy={policy}
          templateData={templateData}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      );
    case 'registry-docker':
      return (
        <DockerRegistryPolicyForm
          mode={mode}
          onSubmit={onSubmit}
          policy={policy}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      );
    case 'security-k8s':
      return (
        <KubernetesSecurityPolicyForm
          mode={mode}
          onSubmit={onSubmit}
          policy={policy}
          templateData={templateData}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      );
    case 'registry-k8s':
      return (
        <KubernetesRegistryPolicyForm
          mode={mode}
          onSubmit={onSubmit}
          policy={policy}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      );
    case 'rbac-k8s':
      return (
        <KubernetesRBACPolicyForm
          mode={mode}
          onSubmit={onSubmit}
          policy={policy}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      );
    case 'setup-k8s':
      return (
        <KubernetesSetupPolicyForm
          mode={mode}
          onSubmit={onSubmit}
          policy={policy}
          onBack={onBack}
          templateData={templateData}
          canGoBack={canGoBack}
        />
      );
    case 'cleanup-docker':
      return (
        <DockerCleanupPolicyForm
          mode={mode}
          onSubmit={onSubmit}
          policy={policy}
          templateData={templateData}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      );
    case 'observability-k8s':
      return (
        <ObservabilityK8sPolicyForm
          mode={mode}
          onSubmit={onSubmit}
          policy={policy}
          templateData={templateData}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      );
    default:
      return null;
  }
}
